/**
 * Canonical form for a guest/contact field value: leading and trailing
 * whitespace removed, everything inside left alone.
 *
 * PORT of website-builder-backend `src/lib/normalize-field-value.ts` (audit
 * 2026-09-23, 4c). The Worker normalizes a submitted value on arrival and the
 * authored condition operands before it evaluates visibility / editability /
 * option conditions; the runtime previewed the same conditions on the RAW
 * typed value. A trailing space in either the operand (`"DE "`) or the typed
 * value therefore hid a field on the site that the Worker then judged
 * visible — or showed one the Worker stripped. The two must see one string.
 *
 * WHY NOT `String.prototype.trim()`. The same class has to exist in Java
 * (main-backend) and TypeScript, and neither language's built-in covers the
 * set: Java's `trim()` stops at U+0020 and `strip()` excludes NBSP, while JS
 * `\s` excludes U+200B. In TS the only character this gains over `.trim()` is
 * U+200B — measured — which is why the shared fixture pins it explicitly.
 *
 * Keep this character class byte-for-byte in sync with the Worker, main-frontend
 * (`src/lib/utils/input-validator.ts`), main-consumer and main-backend. Parity
 * is asserted per case by `__fixtures__/normalize-field-value.json`, which
 * both repos load (Prettier reformats the code, so bytes cannot be compared).
 *
 * INTERNAL whitespace is content and is never collapsed — "Gänsemarkt 21-23"
 * must survive untouched. A value that is nothing but whitespace becomes "".
 */
const EDGE_WS_CLASS =
  "[\\t\\n\\v\\f\\r \\u00a0\\u1680\\u2000-\\u200a\\u200b\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff]";
const EDGE_WHITESPACE = new RegExp(`^${EDGE_WS_CLASS}+|${EDGE_WS_CLASS}+$`, "g");

export function normalizeFieldValue(value: string): string {
  return value.replace(EDGE_WHITESPACE, "");
}

/** Normalize when it is a string, pass everything else through untouched. */
export function normalizeIfString<T>(value: T): T {
  return typeof value === "string"
    ? (normalizeFieldValue(value) as unknown as T)
    : value;
}

/**
 * Edge-whitespace normalization of an ALREADY-BUILT flat map — here the live
 * guest values the condition evaluator reads (session prefill + typed values).
 * Descends one level into plain records (the `additionalFields` sub-document)
 * and ONLY into plain records: arrays are mapped element-wise, everything
 * else (numbers, booleans, the IMAGE sentinel object) passes through.
 */
export function normalizeFlatMap(
  map: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(map)) {
    out[key] = isPlainRecord(value)
      ? normalizeFlatMap(value)
      : Array.isArray(value)
        ? value.map((el) => normalizeIfString(el))
        : normalizeIfString(value);
  }
  return out;
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== "object" || Array.isArray(v)) return false;
  const proto: unknown = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/**
 * Edge-whitespace normalization for a flat submitted value map. Mirrors the
 * Worker's `normalizeValueMap`: shallow, string-only, objects never entered,
 * and the enum-valued keys in `skip` left exactly as typed because their
 * spelling IS the validation guard.
 */
export function normalizeValueMap(
  values: Record<string, unknown>,
  skip: ReadonlySet<string> = NEVER_NORMALIZE,
): Record<string, unknown> {
  // Null-prototype target, as in the Worker: `out[key] =` on a plain object
  // invokes the inherited `__proto__` SETTER, so a `__proto__` key would stop
  // being an own property and silently become the map's prototype.
  const out: Record<string, unknown> = Object.create(null) as Record<
    string,
    unknown
  >;
  for (const [key, value] of Object.entries(values)) {
    if (skip.has(key)) {
      out[key] = value;
      continue;
    }
    out[key] = Array.isArray(value)
      ? value.map((el) => normalizeIfString(el))
      : normalizeIfString(value);
  }
  return out;
}

/** Enum-valued keys whose exact spelling is a validation guard. */
export const NEVER_NORMALIZE: ReadonlySet<string> = new Set(["state"]);

/**
 * The live guest map as the condition evaluator must see it: top-level values
 * like a submitted map (`normalizeValueMap` — `state` untouched, strings and
 * string arrays trimmed), and the nested `additionalFields` record like a
 * stored guest (`normalizeFlatMap`). This is the union of the two Worker
 * paths that feed `evaluateConditionNormalized`, because the runtime's live
 * map is exactly that union: session prefill + values typed so far.
 */
export function normalizeConditionData(
  map: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(map)) {
    if (NEVER_NORMALIZE.has(key)) {
      out[key] = value;
      continue;
    }
    out[key] = isPlainRecord(value)
      ? normalizeFlatMap(value)
      : Array.isArray(value)
        ? value.map((el) => normalizeIfString(el))
        : normalizeIfString(value);
  }
  return out;
}
